import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import AvatarIcon from "./AvatarIcon";
import BrandIcon from "./BrandIcon";
import { tokens } from "../theme";

interface ChatHeaderProps {
  onReset: () => void;
  disabled?: boolean;
  /** From GET /v1/config.name — used in "Chat with {{name}}" */
  name?: string;
}

const styles: Record<string, CSSProperties> = {
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[3],
    padding: `${tokens.space[4]} ${tokens.space[4]} ${tokens.space[5]}`,
    background: tokens.surface.header,
    color: tokens.text.onBrand,
    flexShrink: 0,
  },
  brandMark: {
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  textBlock: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: tokens.font.size.lg,
    fontWeight: tokens.font.weight.bold,
    letterSpacing: "-0.01em",
    lineHeight: tokens.font.lineHeight.tight,
  },
  subtitle: {
    margin: "2px 0 0",
    fontSize: tokens.font.size.sm,
    fontWeight: tokens.font.weight.regular,
    opacity: 0.92,
    lineHeight: 1.3,
  },
  resetBtn: {
    width: 36,
    height: 36,
    border: "none",
    background: "transparent",
    color: tokens.text.onBrand,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    padding: 0,
    alignSelf: "flex-start",
  },
};

export default function ChatHeader({
  onReset,
  disabled,
  name,
}: ChatHeaderProps) {
  const { t } = useTranslation();
  const displayName = name?.trim() || t("header.brandAlt");

  return (
    <header style={styles.header}>
      <div style={styles.brandMark}>
        <AvatarIcon variant="onBrand" size={40} alt={displayName} />
      </div>
      <div style={styles.textBlock}>
        <h1 style={styles.title}>{t("header.title", { name: displayName })}</h1>
        <p style={styles.subtitle}>{t("header.subtitle")}</p>
      </div>
      <button
        type="button"
        style={{
          ...styles.resetBtn,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        onClick={onReset}
        disabled={disabled}
        aria-label={t("header.reset")}
        title={t("header.reset")}
      >
        <BrandIcon name="refreshWhite" size={22} />
      </button>
    </header>
  );
}
